import { Loader2, Save, X } from "lucide-react";
import { useState } from "react";
import type { OAuthClient, OAuthClientUpdate } from "@/types";

interface EditOAuthClientModalProps {
  client: OAuthClient;
  saving: boolean;
  onClose: () => void;
  /** Validation errors are surfaced via toast by the caller. */
  onValidationError: (message: string) => void;
  onSave: (uuid: string, payload: OAuthClientUpdate) => void;
}

export function EditOAuthClientModal({
  client,
  saving,
  onClose,
  onValidationError,
  onSave,
}: Readonly<EditOAuthClientModalProps>) {
  const [clientName, setClientName] = useState(client.clientName);
  const [redirectText, setRedirectText] = useState(client.redirectUris.join("\n"));

  const handleSubmit = () => {
    const redirectUris = redirectText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!clientName.trim()) {
      onValidationError("Client name is required");
      return;
    }
    if (redirectUris.length === 0) {
      onValidationError("At least one redirect URI is required");
      return;
    }
    onSave(client.uuid, { clientName: clientName.trim(), redirectUris });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-surface-secondary border border-border-primary p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-content-primary">Edit OAuth Client</h3>
          <button type="button" onClick={onClose}>
            <X className="h-5 w-5 text-content-tertiary" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="block text-xs font-medium text-content-tertiary mb-1">Client ID</div>
            <div className="rounded-md bg-surface-primary border border-border-primary px-3 py-2 text-sm text-content-tertiary font-mono no-ligatures break-all">
              {client.clientId}
            </div>
          </div>
          <div>
            <label htmlFor="oc-edit-name" className="block text-xs font-medium text-content-tertiary mb-1">
              Name
            </label>
            <input
              id="oc-edit-name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="w-full rounded-md bg-surface-primary border border-border-primary px-3 py-2 text-sm text-content-primary"
              placeholder="My External App"
            />
          </div>
          <div>
            <label htmlFor="oc-edit-redirects" className="block text-xs font-medium text-content-tertiary mb-1">
              Redirect URIs (one per line, exact match)
            </label>
            <textarea
              id="oc-edit-redirects"
              value={redirectText}
              onChange={(e) => setRedirectText(e.target.value)}
              rows={3}
              className="w-full rounded-md bg-surface-primary border border-border-primary px-3 py-2 text-sm text-content-primary font-mono no-ligatures"
              placeholder={"https://app.example.com/auth/callback"}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-md text-sm text-content-tertiary hover:text-content-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-brand-primary text-white text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
